import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Image, message, Form } from "antd";
import { GoogleOutlined } from "@ant-design/icons";
import cache from "utils/cache";
import {
  authApi,
  useLoginMutation,
  useLogoutUserMutation,
  useSignUpMutation,
} from "api/authApi";
import {
  EMAIL_RULES,
  EMAIL_VALIDATE_TRIGGER,
  EMAIL_VERIFICATION_LOGIN_MESSAGE,
  PASSWORD_RULES,
  PHONE_RULES,
  confirmPasswordRules,
  firstNameRules,
  lastNameRules,
} from "utils/validation";
import SignInImage from "@/assets/Images/signIn.png";
import {
  MainLayout,
  LeftLayout,
  StyledModal,
  RightLayout,
  Heading,
  SubHeading,
  StyledButton,
  LoginButtonWrapper,
  StyledDivider,
  EmailText,
  StyledInput,
  StyledPasswordInput,
  InputWrapper,
  NameRow,
  StyledContinueButton,
  TermAndConditionsText,
} from "./styles";
import { SwitchAuthText, SwitchAuthLink } from "./styles";

const SignInModal = ({
  isModalOpen,
  toggleModal,
  isSignUpMode,
  onSwitchMode,
  inviteToken = "",
  invitedEmail = "",
  inviteWarning = "",
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [login, { isLoading: isLoginLoading }] = useLoginMutation();
  const [logoutUser] = useLogoutUserMutation();
  const [signUp, { isLoading: isSignUpLoading }] = useSignUpMutation();
  const isLoading = isLoginLoading || isSignUpLoading;

  useEffect(() => {
    if (!isModalOpen) {
      form.resetFields();
    }
  }, [isModalOpen, form]);

  useEffect(() => {
    form.resetFields();
  }, [isSignUpMode, form]);

  useEffect(() => {
    if (isSignUpMode && invitedEmail) {
      form.setFieldsValue({ email: invitedEmail });
    }
  }, [form, invitedEmail, isSignUpMode, isModalOpen]);

  const getApiErrorMessage = (err) => {
    const messagePayload = err?.data?.message;
    if (Array.isArray(messagePayload)) return messagePayload[0];
    if (typeof messagePayload === "string") return messagePayload;
    if (typeof messagePayload?.message === "string") return messagePayload.message;
    return err?.message || "Something went wrong. Please try again.";
  };

  const handleContinue = async () => {
    const fieldsToValidate = isSignUpMode
      ? [
          "firstName",
          "lastName",
          "phoneNumber",
          "email",
          "password",
          "confirmPassword",
        ]
      : ["email", "password"];

    let values;
    try {
      values = await form.validateFields(fieldsToValidate);
    } catch {
      return;
    }

    const { email, password, firstName, lastName, phoneNumber } = values;

    if (isSignUpMode) {
      try {
        await signUp({
          email,
          password,
          firstName,
          lastName,
          phoneNumber,
          ...(inviteToken ? { inviteToken } : {}),
        }).unwrap();
        message.success(
          inviteToken
            ? "Sponsored account created. Check your email for login details, then sign in."
            : "Account created! Please check your email to verify your account, then log in."
        );
        form.resetFields();
        toggleModal();
      } catch (err) {
        message.error("Sign up failed: " + getApiErrorMessage(err));
      }
    } else {
      try {
        const result = await login({ email, password }).unwrap();

        if (result?.user?.verified === false) {
          cache.removeItem("user");
          try {
            await logoutUser().unwrap();
          } catch {
            // clear session cookie if backend set one before responding
          }
          message.warning(EMAIL_VERIFICATION_LOGIN_MESSAGE);
          return;
        }

        if (result?.user?.role === "DROPSHIPPER" || result?.user?.role === "SUPPLIER") {
          cache.setItem("user", result.user);
          dispatch(
            authApi.util.upsertQueryData("verifyUser", undefined, result.user)
          );
          const destination = result.user.firstTimeLogin ? "/sign-up" : "/home";
          navigate(destination, { replace: true });
          form.resetFields();
          toggleModal();
        } else {
          cache.removeItem("user");
          message.error("Unauthorized user role");
        }
      } catch (err) {
        const apiMessage = getApiErrorMessage(err);
        if (err?.status === 403) {
          message.warning(apiMessage);
          return;
        }
        message.error("Login failed: " + apiMessage);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleContinue();
    }
  };

  return (
    <StyledModal
      open={isModalOpen}
      onCancel={toggleModal}
      footer={false}
      centered
    >
      <MainLayout>
        <LeftLayout>
          <Image
            style={{
              height: "39.5rem",
              borderBottomLeftRadius: "8px",
              borderTopLeftRadius: "8px",
            }}
            preview={false}
            alt="Image"
            src={SignInImage}
          />
        </LeftLayout>
        <RightLayout>
          <Heading>
            {isSignUpMode ? "Create an Account" : "Welcome Back"}
          </Heading>
          <SubHeading>
            {isSignUpMode
              ? inviteToken
                ? "Complete registration to activate your sponsored account. No paid plan required."
                : "Join us for immediate access to the best quality on-trend products."
              : "Immediate access to best quality on-trend products, sourced from trusted suppliers."}
          </SubHeading>
          {inviteWarning ? (
            <p style={{ color: "#b45309", marginBottom: 12 }}>{inviteWarning}</p>
          ) : null}
          <LoginButtonWrapper>
            <StyledButton
              size="large"
              icon={<GoogleOutlined />}
              onClick={() =>
                (window.location.href = `${process.env.REACT_APP_BASE_URL}auth/google`)
              }
            >
              {isSignUpMode ? "Sign up with Google" : "Login with Google"}
            </StyledButton>
          </LoginButtonWrapper>
          <StyledDivider plain>Or</StyledDivider>
          <Form form={form} layout="vertical" onFinish={handleContinue}>
            <InputWrapper>
              {isSignUpMode && (
                <>
                  <NameRow>
                    <Form.Item name="firstName" rules={firstNameRules}>
                      <StyledInput
                        placeholder="First Name"
                        onKeyDown={handleKeyPress}
                        autoComplete="given-name"
                      />
                    </Form.Item>
                    <Form.Item name="lastName" rules={lastNameRules}>
                      <StyledInput
                        placeholder="Last Name"
                        onKeyDown={handleKeyPress}
                        autoComplete="family-name"
                      />
                    </Form.Item>
                  </NameRow>
                  <Form.Item name="phoneNumber" rules={PHONE_RULES}>
                    <StyledInput
                      placeholder="Phone Number"
                      onKeyDown={handleKeyPress}
                      autoComplete="tel"
                    />
                  </Form.Item>
                </>
              )}
              {!isSignUpMode && <EmailText>Email Address</EmailText>}
              <Form.Item
                name="email"
                rules={EMAIL_RULES}
                validateTrigger={EMAIL_VALIDATE_TRIGGER}
              >
                <StyledInput
                  type="email"
                  placeholder="Email Address"
                  onKeyDown={handleKeyPress}
                  autoComplete="email"
                  readOnly={Boolean(isSignUpMode && invitedEmail)}
                />
              </Form.Item>
              <Form.Item name="password" rules={PASSWORD_RULES}>
                <StyledPasswordInput
                  placeholder="Password"
                  visibilityToggle
                  onKeyDown={handleKeyPress}
                  autoComplete={isSignUpMode ? "new-password" : "current-password"}
                />
              </Form.Item>
              {isSignUpMode && (
                <Form.Item
                  name="confirmPassword"
                  dependencies={["password"]}
                  rules={confirmPasswordRules("password")}
                >
                  <StyledPasswordInput
                    placeholder="Confirm Password"
                    visibilityToggle
                    onKeyDown={handleKeyPress}
                    autoComplete="new-password"
                  />
                </Form.Item>
              )}
            </InputWrapper>
            <LoginButtonWrapper>
              <StyledContinueButton
                size="large"
                htmlType="submit"
                loading={isLoading}
              >
                {isSignUpMode ? "Sign Up" : "Login"}
              </StyledContinueButton>
            </LoginButtonWrapper>
          </Form>
          {typeof onSwitchMode === "function" && (
            <SwitchAuthText>
              {isSignUpMode ? (
                <>
                  Already have an account?
                  <SwitchAuthLink onClick={onSwitchMode}> Log in</SwitchAuthLink>
                </>
              ) : (
                <>
                  Don't have an account?
                  <SwitchAuthLink onClick={onSwitchMode}> Sign up</SwitchAuthLink>
                </>
              )}
            </SwitchAuthText>
          )}
          <TermAndConditionsText>
            By clicking continue, you agree to our Terms and Conditions and
            Privacy policy.
          </TermAndConditionsText>
        </RightLayout>
      </MainLayout>
    </StyledModal>
  );
};

export default SignInModal;
